import { useState, type FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { BrandMark } from "../components/BrandMark";
import { MIN_PASSWORD_LENGTH, setAccountPassword, validateNewPassword } from "./set-password";

/**
 * Shown once, right after an officer follows an Auth invite / recovery link into Guard Web. The
 * link signed them in but left the account without a password, so this is not skippable: leaving
 * here without one means never being able to sign in again on this or the native app.
 */
export function SetPasswordPanel({
  client,
  email,
  onComplete,
  onSignOut,
}: {
  readonly client: SupabaseClient;
  readonly email: string | null;
  readonly onComplete: () => void;
  readonly onSignOut: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const validation = validateNewPassword({ password, confirmation });
    if (!validation.ok) {
      setError(validation.reason);
      return;
    }

    setBusy(true);
    const result = await setAccountPassword(client, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    setPassword("");
    setConfirmation("");
    onComplete();
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={handleSubmit} aria-labelledby="setpw-title" noValidate>
        <header className="signin__brand">
          <BrandMark className="signin__mark" />
          <p className="signin__eyebrow">SecOps</p>
          <h1 id="setpw-title" className="signin__title">
            Choose a password
          </h1>
          <p className="signin__subtitle">
            {email === null ? "Finish setting up this account." : `Finish setting up ${email}.`}
          </p>
        </header>
        <p className="join__hint">
          You will use this password to sign in on the SecOps Guard app and here.
        </p>

        <div className="signin__field">
          <label htmlFor="setpw-password">New password</label>
          <div className="signin__password">
            <input
              id="setpw-password"
              type={visible ? "text" : "password"}
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
            <button
              className="signin__toggle"
              type="button"
              aria-label={visible ? "Hide password" : "Show password"}
              aria-pressed={visible}
              onClick={() => setVisible((shown) => !shown)}
              disabled={busy}
            >
              {visible ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <label className="signin__field">
          <span>Confirm password</span>
          <input
            type={visible ? "text" : "password"}
            name="confirm-password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={busy}
            required
          />
        </label>

        {error ? (
          <p className="signin__error" role="alert">
            {error}
          </p>
        ) : null}

        <button className="signin__submit" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save password"}
        </button>
        <button className="join__ghost" type="button" onClick={onSignOut} disabled={busy}>
          Sign out
        </button>
      </form>
    </div>
  );
}
